import { useEffect } from "react";
import type { Notification } from "stream-chat";
import { useChatContext, useTranslationContext } from "stream-chat-react";

const getNotificationKey = ({ severity, type, message }: Notification) =>
  `${severity ?? ""}|${type ?? ""}|${message}`;

/**
 * Housekeeping for Stream's client notifications (the dark toasts above the message list), which
 * the SDK leaves half-finished in this version:
 *
 * - Auto-dismiss. Every notification carries a `duration`, but nothing in stream-chat or
 *   stream-chat-react ever calls `startTimeout`, and the toast has no close button — so an error
 *   stays on screen until the channel is left. The timer is started here, once per notification.
 * - De-duplication. Some failures fire per pointer event (scrubbing a waveform), stacking a
 *   screenful of identical toasts; the same message at the same severity is kept once, oldest copy.
 * - The seek "error" is dropped. The audio player refuses to seek while the element has no
 *   duration yet — the very first pointer event on a recording that has not been played — and
 *   reports "Cannot seek in the recording", even though the seek succeeds a moment later once
 *   metadata arrives. The plugin that emits it is re-registered by the SDK on every render, so it
 *   cannot be swapped out; matching the message through the SAME `t` the plugin used is exact.
 *
 * Must run inside `<Chat>`.
 */
export const useStreamClientNotifications = () => {
  const { client } = useChatContext();
  const { t } = useTranslationContext();

  useEffect(() => {
    const { notifications: manager } = client;
    const seekNoise = t("Cannot seek in the recording");
    const startedTimeouts = new Set<string>();

    const prune = ({ notifications }: { notifications: Notification[] }) => {
      const seen = new Set<string>();
      const toRemove: Array<string> = [];

      for (const notification of notifications) {
        const key = getNotificationKey(notification);
        if (notification.message === seekNoise || seen.has(key)) {
          toRemove.push(notification.id);
          continue;
        }
        seen.add(key);
        if (!startedTimeouts.has(notification.id)) {
          startedTimeouts.add(notification.id);
          manager.startTimeout(notification.id);
        }
      }

      // Each removal re-emits the store; the next pass then finds nothing to prune and stops.
      for (const id of toRemove) {
        manager.remove(id);
      }
    };

    const unsubscribe = manager.store.subscribe(prune);
    prune(manager.store.getLatestValue());
    return unsubscribe;
  }, [client, t]);
};
